import { useAppDispatch, useAppSelector } from "src/common/state/hooks";
import { allTeamSelector, getTeam } from "../state/teamSlice";
import type { RootState } from "../../../common/state/store";
import TableComp from "../../../common/components/Table/Table";
import type { TableCheckboxCell, TableRow } from "../../../common/components/Table/types";
import type { ITeamMember } from "../state/teamInterfaces";
import { Checkbox } from "../../../common/components/Input";
import type { Dispatch, MouseEvent, SetStateAction } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";
import { getCurrentOrganization, getCurrentUser } from "../../profile/state/profileSlice";
import { closeStandardModal } from "../../../common/state/slice/modal/modalSlice";
import { getCurrentProjectPermissions } from "../../projects/state/projectsSlice";
import { getCurrentProjectGroupPermissions } from "../../projectGroups/state/projectGroupsSlice";
import { ORGANIZATION_COMPONENT_ID } from "src/pages/Profile/Organization";

export interface DynamicTableFormValues {
  inviteeId: string[];
}

type ITeamRow = TableRow & {
  checkbox: TableCheckboxCell;
  firstName: string;
  lastName: string;
  email: string;
};

interface IDynamicTableProps {
  onSubmit: (data: DynamicTableFormValues) => void | Promise<void>;
  className?: string;
  setIsEmpty?: Dispatch<SetStateAction<boolean>>;
}

const DynamicTable = ({ onSubmit, className = "", setIsEmpty }: IDynamicTableProps) => {
  const { t } = useTranslation();
  const location = useLocation();
  const currentUser = useAppSelector(getCurrentUser);
  const dispatch = useAppDispatch();

  const { team, currentProjectPermissions, currentProjectGroupPermissions, organization } =
    useAppSelector((state: RootState) => {
      return {
        team: allTeamSelector(state),
        currentProjectPermissions: getCurrentProjectPermissions(state),
        currentProjectGroupPermissions: getCurrentProjectGroupPermissions(state),
        organization: getCurrentOrganization(state),
      };
    });

  useEffect(() => {
    if (organization?.id) {
      void dispatch(getTeam({ orgId: organization.id, componentId: ORGANIZATION_COMPONENT_ID }));
    }
  }, [dispatch, organization?.id]);

  const [columnValues, setColumnValues] = useState<ITeamRow[]>([]);
  const [scrollPosition, setScrollPosition] = useState(0);

  const [isCheck, setIsCheck] = useState<string[]>([]);

  const { register, handleSubmit, watch } = useForm<DynamicTableFormValues>({
    defaultValues: {
      inviteeId: isCheck,
    },
  });

  const isInCollabPage = location.pathname.toLowerCase().includes("team");

  const permittedUserIds = useMemo(() => {
    const ids = new Set<number>();
    [...currentProjectPermissions, ...currentProjectGroupPermissions].forEach((p) => {
      const id = Number(p?.user?.id);
      if (!Number.isNaN(id)) ids.add(id);
    });
    return ids;
  }, [currentProjectPermissions, currentProjectGroupPermissions]);

  const permittedMemberIds = useMemo(
    () => new Set(currentProjectPermissions.map((permission) => permission.user?.id)),
    [currentProjectPermissions],
  );

  const updatedList = team.filter((elt) => {
    const emailRegex = /^[0-9]{4}$/;
    if (emailRegex.test(elt.email)) {
      return false;
    }

    return (
      !permittedUserIds.has(Number(elt?.id)) &&
      typeof elt.role === "object" &&
      elt.role?.name === (isInCollabPage ? "Creator" : "Enduser") &&
      Number(currentUser?.id) !== Number(elt.id) &&
      !elt.email.includes("@sneakysneaky.ua")
    );
  });
  const [teamList, setTeamList] = useState<ITeamMember[]>(updatedList);

  useEffect(() => {
    setTeamList(updatedList);
  }, []);

  const watchAllFields = watch("inviteeId");

  const isDirty = watchAllFields?.length > 0;

  const handleClick = (e: MouseEvent<HTMLInputElement>) => {
    const target = e.target as HTMLInputElement;
    const { id, checked } = target;

    if (tableContainerRef.current) {
      const current = tableContainerRef.current;
      setScrollPosition(current.scrollTop);
    }

    if (checked) {
      const nextValues = [...isCheck, id];
      setIsCheck(nextValues);
    } else {
      setIsCheck(isCheck.filter((item) => item !== id));
    }
  };

  useEffect(() => {
    if (tableContainerRef.current) {
      const current = tableContainerRef.current;
      current.scrollTop = scrollPosition;
    }
  }, [isCheck]);
  const colValues: ITeamRow[] =
    teamList &&
    teamList.map((member: ITeamMember, i: number) => {
      return {
        checkbox: (
          <Checkbox
            defaultChecked={permittedMemberIds.has(member?.id)}
            checked={isCheck.includes(`${member.id}`)}
            key={`${i}-${member.id}`}
            id={`${member.id}`}
            value={member.id}
            registerFct={() => register("inviteeId")}
            onClick={(e) => {
              e.stopPropagation();
              handleClick(e);
            }}
          />
        ),
        firstName: member.firstname,
        lastName: member.lastname,
        email: member.email,
      };
    });

  useEffect(() => {
    setIsCheck(
      teamList
        .filter((member) => permittedMemberIds.has(member?.id))
        .map((member) => `${member?.id}`),
    );

    setColumnValues(colValues);

    if (colValues.length === 0 && setIsEmpty) {
      setIsEmpty(true);
    }
  }, [location]);

  const colHeaders = [t("general.firstname"), t("general.lastname"), t("general.email")];
  const tableContainerRef = useRef<HTMLDivElement>(null);

  return (
    <form
      className={`flex flex-col ${className} overflow-auto`}
      onSubmit={(e) => e.preventDefault()}
      key={`?${isCheck.join("marlon")}`}
    >
      {columnValues?.length > 0 ? (
        <div className="flex overflow-auto h-[93%]">
          <TableComp
            selectFromListInTable={true}
            isAllCollaborators={true}
            isCollaboratorAndParticipant={true}
            rows={colValues}
            colHeaders={colHeaders}
            className="w-full"
            ref={tableContainerRef}
            listTofilter={teamList}
            setListOfItemsFilter={setTeamList}
          />
        </div>
      ) : (
        <h2 className="mb-2 mt-2 text-center">
          {isInCollabPage
            ? t("pages.project.inviteCollaborators.noMembersToInvite")
            : t("pages.project.inviteParticipants.noMembersToInvite")}
        </h2>
      )}

      <div className="w-full flex justify-end">
        <button
          type="button"
          className="btn-alternative-outline mt-2"
          onClick={() => dispatch(closeStandardModal("add-team"))}
        >
          {t("general.cancel")}
        </button>
        {columnValues?.length > 0 && (
          <button
            disabled={!isDirty}
            type="button"
            onClick={() => handleSubmit(onSubmit)()}
            className="btn-primary-fill mt-2 ml-2"
          >
            {t("general.confirm")}
          </button>
        )}
      </div>
    </form>
  );
};
export default DynamicTable;
